import { DragEvent } from 'react';
import {
  CustomNode,
  NodeCategory,
  NodeStruct,
  NodeInputPort,
  NodeOutputPort,
} from '@/types/node-types';
import { Connection } from '@xyflow/react';
import { getAbsolutePosition } from '@/pages/workflow/helper/group-nodes';

const NODE_WIDTH = 300;
const TARGET_GAP = 80;
export const VERTICAL_STACK_GAP = 120;

export type ConnectionDirection = 'forward' | 'reverse';

export interface AutoConnectionResult {
  direction: ConnectionDirection;
  sourceHandle: string;
  targetHandle: string;
}

/**
 * Collects all input ports from a node struct, including both regular
 * input_ports and bindable param ports (e.g. the "Task" slot on Agent nodes).
 */
function getAllInputPorts(struct: NodeStruct): NodeInputPort[] {
  const bindableInputPorts =
    struct.params
      ?.filter((param) => param.bindable_config?.input_port)
      .map((param) => param.bindable_config!.input_port) ?? [];
  return [...struct.input_ports, ...bindableInputPorts];
}

/**
 * Determines whether a dropped node can auto-connect to a target node,
 * and if so, which direction and port pair to use.
 *
 * - "forward": dropped node is source (its output → target's input)
 * - "reverse": dropped node is target (target's output → dropped node's input)
 *
 * Returns null when the connection is ambiguous (both directions are valid,
 * e.g. Agent → Agent) or when no compatible ports exist.
 */
export function resolveAutoConnection(
  droppedStruct: NodeStruct,
  targetStruct: NodeStruct,
): AutoConnectionResult | null {
  const forwardPair = findFirstCompatiblePair(
    droppedStruct.output_ports,
    getAllInputPorts(targetStruct),
  );
  const reversePair = findFirstCompatiblePair(
    targetStruct.output_ports,
    getAllInputPorts(droppedStruct),
  );

  if (forwardPair && !reversePair) {
    return {
      direction: 'forward',
      sourceHandle: forwardPair.outputPortId,
      targetHandle: forwardPair.inputPortId,
    };
  }
  if (reversePair && !forwardPair) {
    return {
      direction: 'reverse',
      sourceHandle: reversePair.outputPortId,
      targetHandle: reversePair.inputPortId,
    };
  }

  // Both directions valid -- use node category as tiebreaker
  if (forwardPair && reversePair) {
    const category = droppedStruct.node_type;
    if (category === NodeCategory.INPUT || category === NodeCategory.TOOL) {
      return {
        direction: 'forward',
        sourceHandle: forwardPair.outputPortId,
        targetHandle: forwardPair.inputPortId,
      };
    }
    if (category === NodeCategory.OUTPUT) {
      return {
        direction: 'reverse',
        sourceHandle: reversePair.outputPortId,
        targetHandle: reversePair.inputPortId,
      };
    }
  }

  // Truly ambiguous (e.g. Agent) or no compatible ports → don't auto-connect
  return null;
}

function findFirstCompatiblePair(
  outputPorts: NodeOutputPort[],
  inputPorts: NodeInputPort[],
): { outputPortId: string; inputPortId: string } | null {
  for (const outputPort of outputPorts) {
    for (const inputPort of inputPorts) {
      const hasOverlap = outputPort.port_type.accepted_types.some((t) =>
        inputPort.port_type.accepted_types.includes(t),
      );
      if (hasOverlap) {
        return { outputPortId: outputPort.id, inputPortId: inputPort.id };
      }
    }
  }
  return null;
}

/**
 * Calculates where to place dropped nodes on the canvas relative to a target.
 * - "forward" connections position to the LEFT (source → target's input port).
 * - "reverse" connections position to the RIGHT (target's output → dropped node).
 * - No target: uses the raw cursor position.
 */
export function calculateDropPosition(
  cursorPosition: { x: number; y: number },
  targetNode: CustomNode | undefined,
  nodeCount: number,
  direction: ConnectionDirection = 'forward',
): { x: number; y: number } {
  if (!targetNode) return cursorPosition;

  const stackHeight = (nodeCount - 1) * VERTICAL_STACK_GAP;
  const targetMidY =
    targetNode.position.y + (targetNode.measured?.height ?? 0) / 2;
  const targetWidth = targetNode.measured?.width ?? NODE_WIDTH;

  const x =
    direction === 'forward'
      ? targetNode.position.x - NODE_WIDTH - TARGET_GAP
      : targetNode.position.x + targetWidth + TARGET_GAP;

  return { x, y: targetMidY - stackHeight / 2 };
}

/**
 * Repositions dragged nodes to stack at `basePosition` (an absolute canvas
 * position), converting back to each node's own parent-relative coordinate
 * space if it has a `parentId` — otherwise a grouped node (or one dropped
 * near a target in a different group) would land in the wrong spot, since
 * React Flow always treats `position` as relative to `parentId`. Resolves
 * the parent's absolute position by walking `nodes` itself (getAbsolutePosition)
 * rather than relying on React Flow's measured internals, which can be
 * undefined right after a drag and would otherwise force an unsafe choice
 * between a wrong absolute position or an absolute position with a stale
 * parentId still attached.
 */
export function repositionDraggedNodesAbsolute(
  nodes: CustomNode[],
  draggedNodeIds: string[],
  basePosition: { x: number; y: number },
): CustomNode[] {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  return nodes.map((node) => {
    const dragIdx = draggedNodeIds.indexOf(node.id);
    if (dragIdx === -1) return node;

    const absoluteTarget = {
      x: basePosition.x,
      y: basePosition.y + dragIdx * VERTICAL_STACK_GAP,
    };

    if (!node.parentId) {
      return { ...node, position: absoluteTarget };
    }

    const parent = nodeById.get(node.parentId);
    if (!parent) {
      // Parent isn't in the node tree (shouldn't happen) — clear parentId so
      // the absolute position isn't misinterpreted as parent-relative.
      return { ...node, position: absoluteTarget, parentId: undefined };
    }

    const parentAbsolutePosition = getAbsolutePosition(parent, nodeById);
    return {
      ...node,
      position: {
        x: absoluteTarget.x - parentAbsolutePosition.x,
        y: absoluteTarget.y - parentAbsolutePosition.y,
      },
    };
  });
}

/**
 * Applies an auto-connection between a dropped/dragged node and a target node.
 * Resolves direction and port pair, validates cardinality/type constraints via
 * the optional `isValid` callback, then calls onConnect if everything passes.
 */
export function applyAutoConnection(
  nodeId: string,
  nodeStruct: NodeStruct,
  targetNode: CustomNode | undefined,
  targetStruct: NodeStruct | undefined,
  onConnect: (connection: Connection) => void,
  isValid?: (connection: Connection) => boolean,
) {
  if (!targetNode || !targetStruct) return;
  const result = resolveAutoConnection(nodeStruct, targetStruct);
  if (!result) return;

  const isForward = result.direction === 'forward';
  const connection: Connection = {
    source: isForward ? nodeId : targetNode.id,
    target: isForward ? targetNode.id : nodeId,
    sourceHandle: result.sourceHandle,
    targetHandle: result.targetHandle,
  };

  if (isValid && !isValid(connection)) return;
  onConnect(connection);
}

/**
 * Finds the first canvas node under the cursor using a 1x1 intersection rect.
 * Returns the node ID or null if no node is under the cursor.
 */
export function findDropTargetId(
  event: { clientX: number; clientY: number },
  screenToFlowPosition: (pos: { x: number; y: number }) => {
    x: number;
    y: number;
  },
  getIntersectingNodes: (rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => Array<{ id: string }>,
  excludeIds?: Set<string>,
): string | null {
  const flowPos = screenToFlowPosition({
    x: event.clientX,
    y: event.clientY,
  });
  const hit = getIntersectingNodes({
    x: flowPos.x,
    y: flowPos.y,
    width: 1,
    height: 1,
  }).find((n) => !excludeIds?.has(n.id));
  return hit?.id ?? null;
}

/**
 * Sets a custom drag image showing a count badge when dragging multiple nodes.
 * Creates a small pill-shaped element (e.g. "3 nodes") as the drag preview.
 */
export function setMultiNodeDragImage(
  event: DragEvent<HTMLDivElement>,
  count: number,
) {
  if (count <= 1) return;

  const el = document.createElement('div');
  el.style.cssText = [
    'position:fixed',
    'top:-1000px',
    'left:-1000px',
    'display:flex',
    'align-items:center',
    'gap:6px',
    'padding:8px 14px',
    'background:#3b82f6',
    'color:white',
    'border-radius:10px',
    'font-size:13px',
    'font-weight:600',
    'font-family:system-ui,sans-serif',
    'box-shadow:0 4px 12px rgba(0,0,0,0.15)',
    'white-space:nowrap',
  ].join(';');
  el.textContent = `${count} node${count !== 1 ? 's' : ''}`;

  document.body.appendChild(el);
  event.dataTransfer.setDragImage(el, el.offsetWidth / 2, el.offsetHeight / 2);
  setTimeout(() => document.body.removeChild(el), 0);
}
